import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import type { TeamDetail } from "../../shared/types/api";

const schema = z.object({
  code: z
    .string()
    .min(1, "Enter a short code")
    .max(32, "Keep it under 32 characters")
    .regex(/^[A-Za-z0-9_-]+$/, "Letters, digits, hyphens and underscores only"),
  name: z.string().min(1, "Enter a name").max(150, "That is too long"),
  teamType: z.enum(["HOME_TEAM", "DELIVERY_TEAM", "TEMPORARY_TEAM", "FUNCTIONAL_TEAM"]),
  description: z.string().max(1000, "That is too long").optional(),
});

type FormValues = z.infer<typeof schema>;

/**
 * The type options carry a one-line explanation rather than expecting people to know the
 * difference. Home versus delivery is the distinction that makes multi-team membership work, and
 * choosing wrongly here quietly distorts every capacity report later.
 */
const TYPE_OPTIONS: { value: FormValues["teamType"]; label: string; hint: string }[] = [
  { value: "HOME_TEAM", label: "Home team", hint: "Organisational home, e.g. Backend Engineering" },
  { value: "DELIVERY_TEAM", label: "Delivery team", hint: "A squad delivering a project" },
  { value: "TEMPORARY_TEAM", label: "Temporary team", hint: "Stood up for a specific purpose" },
  { value: "FUNCTIONAL_TEAM", label: "Functional team", hint: "A discipline group, e.g. QA Guild" },
];

export function TeamFormDrawer({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (team: TeamDetail) => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { teamType: "DELIVERY_TEAM" },
  });

  const selectedType = watch("teamType");

  const close = () => {
    reset();
    setFormError(null);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const created = await api.post<TeamDetail>("/api/v1/teams", values);
      onSaved(created);
      close();
    } catch (error) {
      setFormError(
        error instanceof ApiError && error.code === "DUPLICATE_RESOURCE"
          ? "A team with that code already exists."
          : error instanceof ApiError
            ? error.message
            : "Could not create the team.",
      );
    }
  });

  return (
    <Drawer
      open={open}
      onClose={close}
      title="New team"
      subtitle="Teams group people for delivery, and membership is recorded with dates."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => void onSubmit()} loading={isSubmitting}>
            {isSubmitting ? "Creating…" : "Create team"}
          </Button>
        </div>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {formError && <InlineAlert tone="error">{formError}</InlineAlert>}

        <Field
          label="Code"
          required
          error={errors.code?.message}
          hint="A short identifier, e.g. PAYMENTS"
        >
          <Input autoFocus placeholder="PAYMENTS" {...register("code")} />
        </Field>

        <Field label="Name" required error={errors.name?.message}>
          <Input placeholder="Payments Squad" {...register("name")} />
        </Field>

        <Field
          label="Type"
          required
          error={errors.teamType?.message}
          hint={TYPE_OPTIONS.find((option) => option.value === selectedType)?.hint}
        >
          <Select {...register("teamType")}>
            {TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Description" error={errors.description?.message} hint="Optional">
          <Textarea rows={3} {...register("description")} />
        </Field>
      </form>
    </Drawer>
  );
}
